import { useState, useEffect, useCallback, memo } from "react";
import Swal from "sweetalert2";
import AnimacionCarga from "../animacion_de_carga";
import BarraSuperior from "../barra_superior";
import BotonSOS from "../sos/boton_sos";
import TablaGenerica from "../tablas";
import { auth } from "../../../functions/api_firebase";
import "bootstrap-icons/font/bootstrap-icons.css";

// Cuenta de correo con la que el backend envía las notificaciones de CoordiPH (coordiph@).
// A la izquierda la configuración (notificaciones_correo/cuenta) y a la derecha los correos
// enviados por esa cuenta (notificaciones_correo/enviados). Todo pasa por el backend, que
// valida que el usuario sea SUPER. "Probar" envía un correo de prueba real con los datos del
// formulario (sin guardarlos); "Guardar" revisa antes que la cuenta inicie sesión y, si falla,
// pregunta antes de guardar para no dejar los correos caídos.

const URL_CUENTA = "https://coordiph.herokuapp.com/api/cuenta_correo";
const ROJO = "#c0392b";

const COLUMNAS = [
  { label: "Fecha", key: "fecha" },
  { label: "Enviado desde", key: "cuenta" },
  { label: "Asunto", key: "asunto" },
  { label: "Para", key: "para" },
  { label: "Copia", key: "copia" },
  { label: "Copia oculta", key: "copia_oculta" },
  { label: "Estado", key: "estado" },
];

const esSuper = () => localStorage.getItem("Permisos") === "SUPER";

const llamarBackend = async (metodo, ruta = "", datos) => {
  const token = await auth.currentUser?.getIdToken();
  const respuesta = await fetch(`${URL_CUENTA}${ruta}`, {
    method: metodo,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token || ""}` },
    body: datos ? JSON.stringify(datos) : undefined,
  });
  const cuerpo = await respuesta.json().catch(() => ({}));
  if (!respuesta.ok) throw new Error(cuerpo.error || "No se pudo comunicar con el servidor.");
  return cuerpo;
};

// dd/MM/yyyy HH:mm:ss, como el resto de CoordiPH.
const fechaLegible = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const RENDERIZADORES = {
  fecha: (valor) => <span style={{ whiteSpace: "nowrap" }}>{fechaLegible(valor)}</span>,
  estado: (valor, fila) => (
    <span
      title={fila.detalle_error || ""}
      style={{ ...s.estado, ...(valor === "Enviado" ? s.estadoOk : s.estadoError) }}
    >
      <i className={`bi ${valor === "Enviado" ? "bi-check-circle" : "bi-x-circle"}`} style={{ marginRight: 5 }} />
      {valor}
    </span>
  ),
};

const CuentaCorreo = memo(() => {
  const [formulario, setFormulario] = useState({ servidor: "", puerto: "", usuario: "", clave: "" });
  const [guardada, setGuardada] = useState(null);
  const [enviados, setEnviados] = useState([]);
  const [verClave, setVerClave] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [cargandoEnviados, setCargandoEnviados] = useState(false);
  const [ocupado, setOcupado] = useState("");

  const mostrarCuenta = (datos) => {
    setGuardada(datos);
    setFormulario({ servidor: datos.servidor, puerto: datos.puerto, usuario: datos.usuario, clave: datos.clave });
  };

  const capturarEnviados = useCallback(async () => {
    setCargandoEnviados(true);
    try {
      setEnviados(await llamarBackend("GET", "/enviados"));
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.message });
    } finally {
      setCargandoEnviados(false);
    }
  }, []);

  const capturarDatos = useCallback(async () => {
    setCargando(true);
    try {
      mostrarCuenta(await llamarBackend("GET"));
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.message });
    } finally {
      setCargando(false);
    }
    capturarEnviados();
  }, [capturarEnviados]);

  useEffect(() => { if (esSuper()) capturarDatos(); }, [capturarDatos]);

  const cambiar = (campo) => (e) => setFormulario({ ...formulario, [campo]: e.target.value });

  // Valida el formulario y devuelve los datos listos para el backend, o null si falta algo.
  const datosFormulario = () => {
    const { servidor, puerto, usuario, clave } = formulario;
    if (!servidor.trim() || !puerto.trim() || !usuario.trim() || !clave) {
      Swal.fire({ icon: "warning", title: "Faltan datos", text: "Escriba el servidor, el puerto, la cuenta y la contraseña." });
      return null;
    }
    if (!/^\d{1,5}$/.test(puerto.trim())) {
      Swal.fire({ icon: "warning", title: "Puerto no válido", text: "El puerto debe tener solo números. Ejemplo: 465." });
      return null;
    }
    return { servidor: servidor.trim(), puerto: puerto.trim(), usuario: usuario.trim(), clave };
  };

  // true si la cuenta inicia sesión; si no, devuelve el mensaje del error.
  const verificarDatos = async (datos) => {
    try {
      await llamarBackend("POST", "/verificar", datos);
      return true;
    } catch (error) {
      return error.message;
    }
  };

  const probar = useCallback(async () => {
    const datos = datosFormulario();
    if (!datos) return;

    const { value: destino } = await Swal.fire({
      title: "Enviar correo de prueba",
      text: "Se enviará un correo con la cuenta del formulario. Revise que le llegue.",
      input: "email",
      inputLabel: "¿A qué correo lo enviamos?",
      inputValue: localStorage.getItem("CorreoElectronico") || "",
      validationMessage: "Escriba un correo válido",
      showCancelButton: true,
      confirmButtonText: "Enviar prueba",
      cancelButtonText: "Cancelar",
      confirmButtonColor: ROJO,
    });
    if (!destino) return;

    setOcupado("probando");
    Swal.fire({ title: "Enviando correo de prueba...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      await llamarBackend("POST", "/probar", { ...datos, destino });
      Swal.fire({
        icon: "success",
        title: "Correo de prueba enviado",
        html: `<p style="font-size:15px;color:#555;">Revise la bandeja de <strong>${destino}</strong>
          (y la carpeta de spam). Si le llegó, la cuenta funciona.<br>Aún no se ha guardado nada.</p>`,
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "La cuenta no funciona", text: error.message });
    } finally {
      setOcupado("");
      capturarEnviados();
    }
  }, [formulario]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = useCallback(async () => {
    const datos = datosFormulario();
    if (!datos) return;

    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "¿Guardar la cuenta de correo?",
      html: `<p style="font-size:15px;color:#555;">Todos los correos de CoordiPH se enviarán con esta cuenta desde el siguiente envío.</p>`,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: ROJO,
    });
    if (!isConfirmed) return;

    setOcupado("guardando");
    Swal.fire({ title: "Revisando la cuenta...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const resultado = await verificarDatos(datos);

    if (resultado !== true) {
      const { isConfirmed: guardarIgual } = await Swal.fire({
        icon: "error",
        title: "La cuenta no funciona",
        html: `<p style="font-size:15px;color:#555;">${resultado}</p>
          <p style="font-size:15px;color:#555;"><strong>Si la guarda así, los correos de CoordiPH dejarán de salir.</strong></p>`,
        showCancelButton: true,
        confirmButtonText: "Guardar de todos modos",
        cancelButtonText: "No guardar",
        confirmButtonColor: ROJO,
      });
      if (!guardarIgual) {
        setOcupado("");
        return;
      }
    }

    Swal.fire({ title: "Guardando...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      mostrarCuenta(await llamarBackend("PUT", "", datos));
      Swal.fire({ icon: "success", title: "Cuenta guardada", text: "Desde el siguiente correo se usa esta cuenta." });
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se guardó", text: error.message });
    } finally {
      setOcupado("");
    }
  }, [formulario]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!esSuper()) {
    return (
      <div style={s.page}>
        <BarraSuperior />
        <div style={s.sinAcceso}>
          <i className="bi bi-shield-lock" style={{ fontSize: 34, color: "#d1d5db" }} />
          <p style={{ marginTop: 12 }}>Esta sección está disponible únicamente para usuarios SUPER.</p>
        </div>
      </div>
    );
  }

  if (cargando) return <AnimacionCarga />;

  return (
    <div style={s.page}>
      <BarraSuperior />

      <div style={s.contenedor}>
        <header style={s.header}>
          <h1 style={s.headerTitle}>Cuenta de correo</h1>
          <p style={s.headerSub}>
            Cuenta con la que CoordiPH envía los correos de notificación a los usuarios.
          </p>
        </header>

        <div style={s.columnas}>
          <section style={{ ...s.tarjeta, ...s.columnaConfig }}>
            <h2 style={s.seccionTitulo}>Configuración del servidor</h2>

            <div style={s.campo}>
              <label style={s.etiqueta} htmlFor="servidor">Servidor de correo</label>
              <input id="servidor" type="text" style={s.entrada} value={formulario.servidor} onChange={cambiar("servidor")}
                placeholder="mail.coordinarseguridad.com" autoComplete="off" />
            </div>

            <div style={s.campo}>
              <label style={s.etiqueta} htmlFor="puerto">Puerto</label>
              <input id="puerto" type="text" style={s.entrada} value={formulario.puerto} onChange={cambiar("puerto")}
                placeholder="465" inputMode="numeric" autoComplete="off" />
              <span style={s.ayuda}>Normalmente 465. También puede ser 587.</span>
            </div>

            <div style={s.campo}>
              <label style={s.etiqueta} htmlFor="usuario">Cuenta (correo)</label>
              <input id="usuario" type="text" style={s.entrada} value={formulario.usuario} onChange={cambiar("usuario")}
                placeholder="coordiph@coordinarseguridad.com" autoComplete="off" />
            </div>

            <div style={s.campo}>
              <label style={s.etiqueta} htmlFor="clave">Contraseña</label>
              <div style={s.envolturaClave}>
                <input id="clave" type={verClave ? "text" : "password"} style={{ ...s.entrada, paddingRight: 54 }}
                  value={formulario.clave} onChange={cambiar("clave")} autoComplete="new-password" />
                <button type="button" style={s.botonVer} onClick={() => setVerClave(!verClave)}
                  title={verClave ? "Ocultar contraseña" : "Mostrar contraseña"}
                  aria-label={verClave ? "Ocultar contraseña" : "Mostrar contraseña"}>
                  <i className={`bi ${verClave ? "bi-eye-slash-fill" : "bi-eye-fill"}`} />
                </button>
              </div>
            </div>

            {guardada?.fecha_actualizacion && (
              <p style={s.ultimoCambio}>
                <i className="bi bi-clock-history" style={{ marginRight: 6 }} />
                Último cambio: {fechaLegible(guardada.fecha_actualizacion)} por {guardada.actualizado_por}
              </p>
            )}

            <div style={s.botones}>
              <button type="button" style={{ ...s.botonGuardar, ...(ocupado ? s.botonDeshabilitado : {}) }}
                onClick={guardar} disabled={Boolean(ocupado)}>
                <i className="bi bi-save" style={{ marginRight: 8 }} />
                {ocupado === "guardando" ? "Guardando..." : "Guardar"}
              </button>
              <button type="button" style={{ ...s.botonProbar, ...(ocupado ? s.botonDeshabilitado : {}) }}
                onClick={probar} disabled={Boolean(ocupado)}>
                <i className="bi bi-send" style={{ marginRight: 8 }} />
                {ocupado === "probando" ? "Enviando..." : "Probar"}
              </button>
            </div>
          </section>

          <section style={{ ...s.tarjeta, ...s.columnaHistorial }}>
            <div style={s.encabezadoHistorial}>
              <h2 style={{ ...s.seccionTitulo, margin: 0 }}>
                Correos enviados
              </h2>
              <button type="button" style={s.botonActualizar} onClick={capturarEnviados} disabled={cargandoEnviados}>
                <i className="bi bi-arrow-clockwise" style={{ marginRight: 6 }} />
                {cargandoEnviados ? "Cargando..." : "Actualizar"}
              </button>
            </div>
            <p style={s.ayuda}>
              Los últimos 300 correos, el más reciente primero, con la cuenta desde la que salió cada uno.
            </p>

            <TablaGenerica
              datos={enviados}
              titulos={COLUMNAS.map((c) => c.label.toUpperCase())}
              campos={COLUMNAS.map((c) => c.key)}
              elementosPorPagina={50}
              columnaOrdenInicial={0}
              direccionOrdenInicial="desc"
              renderizadores={RENDERIZADORES}
              mostrarBarraSuperior={false}
            />
          </section>
        </div>
      </div>

      <BotonSOS />
    </div>
  );
});

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 100,
  },
  contenedor: { width: "100%", maxWidth: 1600, margin: "0 auto", padding: "0 16px", boxSizing: "border-box" },
  header: { padding: "28px 0 20px", borderBottom: "1px solid #e5e7eb", marginBottom: 24 },
  headerTitle: { margin: 0, fontSize: 24, fontWeight: 700, color: "#1a1a2e" },
  headerSub: { margin: "6px 0 0", fontSize: 16, color: "#6b7280" },
  columnas: { display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" },
  columnaConfig: { flex: "1 1 420px", maxWidth: 600 },
  columnaHistorial: { flex: "999 1 520px", minWidth: 0, overflowX: "auto" },
  tarjeta: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "24px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    boxSizing: "border-box",
  },
  seccionTitulo: { fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: "0 0 20px", textTransform: "uppercase", letterSpacing: 0.5 },
  campo: { marginBottom: 20 },
  etiqueta: { display: "block", marginBottom: 8, color: "#1a1a2e", fontSize: 16, fontWeight: 600 },
  entrada: {
    width: "100%",
    margin: 0,
    padding: "13px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 17,
    color: "#1a1a2e",
    background: "#ffffff",
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
  },
  ayuda: { display: "block", marginTop: 6, fontSize: 14, color: "#6b7280" },
  envolturaClave: { position: "relative" },
  botonVer: {
    position: "absolute",
    top: 0,
    right: 0,
    height: "100%",
    width: 52,
    border: "none",
    background: "transparent",
    color: ROJO,
    fontSize: 22,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ultimoCambio: { fontSize: 14, color: "#6b7280", margin: "0 0 20px" },
  botones: { display: "flex", gap: 16, flexWrap: "wrap" },
  botonProbar: {
    flex: "1 1 160px",
    padding: "15px 24px",
    border: `2px solid ${ROJO}`,
    borderRadius: 10,
    background: "#ffffff",
    color: ROJO,
    fontSize: 17,
    fontWeight: 600,
    cursor: "pointer",
  },
  botonGuardar: {
    flex: "1 1 160px",
    padding: "15px 24px",
    border: "none",
    borderRadius: 10,
    background: ROJO,
    color: "#ffffff",
    fontSize: 17,
    fontWeight: 600,
    cursor: "pointer",
  },
  botonDeshabilitado: { background: "#e5e7eb", color: "#9ca3af", borderColor: "#e5e7eb", cursor: "not-allowed" },
  encabezadoHistorial: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  botonActualizar: {
    padding: "9px 16px",
    border: `1px solid ${ROJO}`,
    borderRadius: 8,
    background: "#ffffff",
    color: ROJO,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  estado: { display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" },
  estadoOk: { background: "#ecfdf5", color: "#047857" },
  estadoError: { background: "#fef2f2", color: "#b91c1c" },
  sinAcceso: { maxWidth: 420, margin: "80px auto", textAlign: "center", color: "#9ca3af", fontSize: 15 },
};

export default CuentaCorreo;
